// UI组件 - 绘制顶部栏、道具区、弹窗等

import type { LevelConfig } from '../types.js';
import { PuzzleGame } from '../core/PuzzleGame.js';

export class UI {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private layout: any;
  private clockImg: HTMLImageElement | null = null;
  private nextImg: HTMLImageElement | null = null;
  private loseImg: HTMLImageElement | null = null;
  private losePicImg: HTMLImageElement | null = null;
  private loseTextImg: HTMLImageElement | null = null;
  private hintData: { pieceId: string; cells: { col: number; row: number }[] }[] | null = null;
  private hintExpireTime: number = 0;
  private fullImage: HTMLImageElement | null = null; // 用于胜利弹窗显示完成效果图

  // 选关弹窗滚动偏移（像素，正=向下滚看后面的关卡）
  levelSelectScrollOffset = 0;

  // UI回调
  onMusicToggle?: () => void;
  onRestart?: () => void;
  onLevelSelect?: () => void;
  onUseMagnet?: () => void;
  onUseHint?: () => void;
  onNextLevel?: () => void;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  setLayout(layout: any): void {
    this.layout = layout;
  }

  setImages(images: {
    clock?: HTMLImageElement;
    next?: HTMLImageElement;
    lose?: HTMLImageElement;
    lose_pic?: HTMLImageElement;
    lose_text?: HTMLImageElement;
  }): void {
    if (images.clock) this.clockImg = images.clock;
    if (images.next) this.nextImg = images.next;
    if (images.lose) this.loseImg = images.lose;
    if (images.lose_pic) this.losePicImg = images.lose_pic;
    if (images.lose_text) this.loseTextImg = images.lose_text;
  }

  setFullImage(img: HTMLImageElement | null): void {
    this.fullImage = img;
  }

  // 绘制顶部栏（两层结构）
  drawTopBar(game: PuzzleGame, isMusicOn: boolean): void {
    const ctx = this.ctx;
    const { gameArea, topBarHeight } = this.layout;
    const barX = gameArea.x;
    const barY = gameArea.y;
    const barWidth = gameArea.width;
    const mainBarH = Math.floor(topBarHeight * 0.6);   // 主栏约 60%
    const timerBarH = topBarHeight - mainBarH;          // 计时条约 40%

    // === 整个顶栏背景 ===
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(barX, barY, barWidth, topBarHeight);

    // === 第一层：主栏 ===
    const mainCenterY = barY + mainBarH / 2;
    const iconSize = Math.floor(mainBarH * 0.64);
    const padding = Math.max(10, iconSize * 0.36);
    const buttonGap = Math.max(6, iconSize * 0.2);

    // 左侧两个按钮：声音 + 重开
    let leftCx = barX + padding + iconSize / 2;
    this.drawIconButton(leftCx, mainCenterY, iconSize, isMusicOn ? 1 : 0); // 声音
    leftCx += iconSize / 2 + buttonGap + iconSize / 2;
    this.drawIconButton(leftCx, mainCenterY, iconSize, 2); // 重开

    // 中间：关卡名称
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${Math.floor(mainBarH * 0.38)}px "KaiTi", "Microsoft YaHei", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`第 ${game.config.levelNumber} 关`, barX + barWidth / 2, mainCenterY);

    // 右侧：选关按钮
    const rightCx = barX + barWidth - padding - iconSize / 2;
    this.drawIconButton(rightCx, mainCenterY, iconSize, 3); // 选关

    // === 第二层：计时条 ===
    const timerY = barY + mainBarH;
    const timerCenterY = timerY + timerBarH / 2;
    this.drawTimerBar(barX, timerY, barWidth, timerBarH, game);
  }

  // 绘制图标按钮（type: 0=声音关, 1=声音开, 2=重开, 3=选关）
  private drawIconButton(cx: number, cy: number, size: number, type: number): void {
    const ctx = this.ctx;
    const half = size / 2;

    // 深色圆形底 + 细描边
    ctx.beginPath();
    ctx.arc(cx, cy, half + 2, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(30, 30, 45, 0.92)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // 用 emoji 字符直接绘制（和道具按钮同一种方式）
    const iconMap: Record<number, string> = {
      0: '🔈',   // 声音关
      1: '🔊',   // 声音开
      2: '↻',   // 重新开始
      3: '📖',   // 选择关卡
    };
    const icon = iconMap[type] ?? '?';

    ctx.fillStyle = '#ffffff';
    ctx.font = `${Math.floor(size * 0.75)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(icon, cx, cy);
  }

  // 绘制第二层：计时条
  private drawTimerBar(x: number, y: number, w: number, h: number, game: PuzzleGame): void {
    const ctx = this.ctx;
    const isLast10 = game.startTime !== null && game.remainingTime <= 10;
    const cy = y + h / 2;
    const padding = 14;

    // 时钟图标
    let iconW = 0;
    if (this.clockImg) {
      const iconSize = 18;
      const iconX = x + padding;
      ctx.drawImage(this.clockImg, iconX, cy - iconSize / 2, iconSize, iconSize);
      iconW = iconSize;
    } else {
      // 没有 clock.png 时画一个简单时钟
      const r = 9;
      const cx = x + padding + r;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // 时针
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx, cy - r + 2);
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // 分针
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + r / 2 - 1, cy);
      ctx.stroke();
      iconW = r * 2;
    }

    // 时间文本
    ctx.save();
    if (isLast10) {
      ctx.globalAlpha = 0.5 + 0.5 * Math.sin(Date.now() / 150);
    }
    ctx.fillStyle = isLast10 ? '#ff4444' : '#ffffff';
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const timeText = PuzzleGame.formatTime(game.remainingTime);
    const textX = x + padding + iconW + 4;
    ctx.fillText(timeText, textX, cy);
    ctx.restore();

    // 进度条（从右到左减少）
    const barX = textX + ctx.measureText(timeText).width + 12;
    const barPaddingRight = 14;
    const barW = x + w - barPaddingRight - barX;
    const barH = 5;
    const barY = cy - barH / 2;

    // 底轨
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, 2.5);
    ctx.fill();

    // 填充（右侧起始，向左缩短）
    const progress = Math.max(0, Math.min(1, game.remainingTime / game.config.timeLimit));
    const fillW = barW * progress;
    ctx.fillStyle = isLast10 ? '#ff4444' : '#4CAF50';
    ctx.beginPath();
    ctx.roundRect(barX + barW - fillW, barY, fillW, barH, 2.5);
    ctx.fill();
  }

  private drawMusicButton(cx: number, cy: number, isOn: boolean): void {
    const ctx = this.ctx;
    const r = 18;

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.fill();

    ctx.fillStyle = isOn ? '#4CAF50' : '#888';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(isOn ? '♪' : '×', cx, cy);
  }
  private drawTimer(cx: number, cy: number, game: PuzzleGame): void {
    const ctx = this.ctx;
    const isLast10 = game.startTime !== null && game.remainingTime <= 10;

    // 钟表图标
    if (this.clockImg) {
      const iconSize = 20;
      ctx.drawImage(this.clockImg, cx - 60, cy - iconSize / 2, iconSize, iconSize);
    }

    // 时间文本
    ctx.fillStyle = isLast10 ? '#ff4444' : '#fff';
    if (isLast10) {
      // 闪烁效果
      const alpha = 0.5 + 0.5 * Math.sin(Date.now() / 150);
      ctx.globalAlpha = alpha;
    }
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(PuzzleGame.formatTime(game.remainingTime), cx - 38, cy);
    ctx.globalAlpha = 1;

    // 进度条
    const barX = cx - 60;
    const barY = cy + 14;
    const barW = 80;
    const barH = 4;
    const progress = Math.max(0, game.remainingTime / game.config.timeLimit);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.fillRect(barX, barY, barW, barH);
    ctx.fillStyle = isLast10 ? '#ff4444' : '#4CAF50';
    // 从左往右填充（时间流逝时右边先缩短）
    ctx.fillRect(barX, barY, barW * progress, barH);
  }

  // 绘制道具区（最底部）
  drawItemArea(game: PuzzleGame): void {
    const ctx = this.ctx;
    const { gameArea, itemAreaHeight } = this.layout;
    // 道具区紧贴底部
    const y = gameArea.y + gameArea.height - itemAreaHeight;

    // 道具区背景 - 深色条
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.fillRect(gameArea.x, y, gameArea.width, itemAreaHeight);

    const buttonSize = Math.floor(itemAreaHeight * 0.8);
    const gap = Math.floor(buttonSize * 0.6);
    const totalWidth = buttonSize * 2 + gap;
    const startX = gameArea.x + (gameArea.width - totalWidth) / 2;
    const cy = y + itemAreaHeight / 2;

    // 磁力道具
    this.drawItemButton(startX + buttonSize / 2, cy, '磁铁', '🧲', game.items.magnet, buttonSize);

    // 提示道具
    this.drawItemButton(startX + buttonSize + gap + buttonSize / 2, cy, '提示', '🔍', game.items.hint, buttonSize);
  }

  private drawItemButton(cx: number, cy: number, label: string, icon: string, count: number, size: number = 40): void {
    const ctx = this.ctx;
    const enabled = count > 0;

    // 外圆（深色）
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 + 4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(15, 15, 25, 0.95)';
    ctx.fill();

    // 内圆（亮色底 / 灰色底）
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
    if (enabled) {
      // 可用：金色渐变底 + 白色描边
      const grad = ctx.createRadialGradient(cx - 3, cy - 3, 2, cx, cy, size / 2);
      grad.addColorStop(0, 'rgba(255, 220, 130, 0.35)');
      grad.addColorStop(1, 'rgba(200, 150, 50, 0.15)');
      ctx.fillStyle = grad;
    } else {
      // 用完：灰色半透明
      ctx.fillStyle = 'rgba(80, 80, 90, 0.4)';
    }
    ctx.fill();
    ctx.strokeStyle = enabled ? 'rgba(255, 210, 120, 0.7)' : 'rgba(120, 120, 130, 0.4)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // 图标（亮/灰）
    ctx.font = '22px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = enabled ? '#ffd270' : '#7a7a88';
    ctx.fillText(icon, cx, cy);

    // 数量角标
    if (enabled) {
      ctx.fillStyle = '#ff4444';
      ctx.beginPath();
      ctx.arc(cx + size / 2 - 4, cy - size / 2 + 4, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px sans-serif';
      ctx.fillText(count.toString(), cx + size / 2 - 4, cy - size / 2 + 5);
    }
  }

  // 弹窗
  drawWonModal(game: PuzzleGame): void {
    const ctx = this.ctx;
    const { gameArea } = this.layout;
    const modalW = gameArea.width * 0.8;
    const modalH = gameArea.height * 0.7;
    const modalX = gameArea.x + (gameArea.width - modalW) / 2;
    const modalY = gameArea.y + (gameArea.height - modalH) / 2;

    // 半透明遮罩
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.fillRect(gameArea.x, gameArea.y, gameArea.width, gameArea.height);

    // 卡片
    ctx.beginPath();
    ctx.roundRect(modalX, modalY, modalW, modalH, 16);
    ctx.fillStyle = '#1e1e3a';
    ctx.fill();
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 3;
    ctx.stroke();

    // 标题
    ctx.fillStyle = '#FFD700';
    ctx.font = 'bold 22px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('挑战成功！', modalX + modalW / 2, modalY + 28);

    // 完成的拼图效果图 - 使用完整图片
    const imgBoxW = modalW * 0.7;
    const imgBoxH = modalH * 0.45;
    const imgBoxX = modalX + (modalW - imgBoxW) / 2;
    const imgBoxY = modalY + 50;

    if (this.fullImage) {
      // 保持原图比例
      const imgRatio = this.fullImage.width / this.fullImage.height;
      let drawW = imgBoxW;
      let drawH = drawW / imgRatio;
      if (drawH > imgBoxH) {
        drawH = imgBoxH;
        drawW = drawH * imgRatio;
      }
      const drawX = imgBoxX + (imgBoxW - drawW) / 2;
      const drawY = imgBoxY + (imgBoxH - drawH) / 2;

      // 圆角图片框
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(drawX, drawY, drawW, drawH, 8);
      ctx.clip();
      ctx.drawImage(this.fullImage, drawX, drawY, drawW, drawH);
      ctx.restore();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(drawX, drawY, drawW, drawH, 8);
      ctx.stroke();
    } else {
      // 占位
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.fillRect(imgBoxX, imgBoxY, imgBoxW, imgBoxH);
    }

    // 用时
    ctx.fillStyle = '#fff';
    ctx.font = '16px sans-serif';
    const totalTime = game.config.timeLimit - game.remainingTime;
    ctx.fillText(`用时：${PuzzleGame.formatTime(totalTime)}`, modalX + modalW / 2, modalY + modalH * 0.72);

    // 最后一关提示
    const isLastLevel = game.config.levelNumber >= game.config.totalLevels;
    if (isLastLevel) {
      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText('恭喜你通关了所有关卡！', modalX + modalW / 2, modalY + modalH * 0.78);
    }

    // "下一关"按钮
    const btnText = isLastLevel ? '返回选关' : '下一关';
    const btnW = 140;
    const btnH = 48;
    const btnX = modalX + (modalW - btnW) / 2;
    const btnY = modalY + modalH - btnH - 25;

    // 绿色按钮（参考 next.png 风格）
    ctx.beginPath();
    ctx.roundRect(btnX, btnY, btnW, btnH, 12);
    ctx.fillStyle = '#4CAF50';
    ctx.fill();
    ctx.strokeStyle = '#66BB6A';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 18px sans-serif';
    ctx.fillText(btnText, btnX + btnW / 2, btnY + btnH / 2);
  }

  drawLostModal(): void {
    const ctx = this.ctx;
    const { gameArea } = this.layout;
    const modalW = gameArea.width * 0.7;
    const modalH = gameArea.height * 0.5;
    const modalX = gameArea.x + (gameArea.width - modalW) / 2;
    const modalY = gameArea.y + (gameArea.height - modalH) / 2;

    // 遮罩
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.fillRect(gameArea.x, gameArea.y, gameArea.width, gameArea.height);

    // 卡片
    ctx.beginPath();
    ctx.roundRect(modalX, modalY, modalW, modalH, 16);
    ctx.fillStyle = '#2a1e1e';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 68, 68, 0.5)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // 失败主图
    if (this.losePicImg) {
      const imgSize = Math.min(modalW * 0.5, 120);
      ctx.drawImage(this.losePicImg, gameArea.x + gameArea.width / 2 - imgSize / 2, modalY + 20, imgSize, imgSize);
    }

    // 失败文字
    ctx.fillStyle = '#ff6666';
    ctx.font = 'bold 20px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('时间到！挑战失败', gameArea.x + gameArea.width / 2, modalY + modalH * 0.55);

    // 重新开始按钮
    const btnW = 140;
    const btnH = 48;
    const btnX = modalX + (modalW - btnW) / 2;
    const btnY = modalY + modalH - btnH - 25;

    ctx.beginPath();
    ctx.roundRect(btnX, btnY, btnW, btnH, 10);
    ctx.fillStyle = '#ff4444';
    ctx.fill();
    ctx.strokeStyle = '#ff6666';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 18px sans-serif';
    ctx.fillText('重新开始', btnX + btnW / 2, btnY + btnH / 2);
  }

  // 确认弹窗（用于重开按钮）
  drawConfirmModal(title: string, message: string, confirmText: string, cancelText: string): void {
    const ctx = this.ctx;
    const { gameArea } = this.layout;
    const modalW = gameArea.width * 0.65;
    const modalH = gameArea.height * 0.35;
    const modalX = gameArea.x + (gameArea.width - modalW) / 2;
    const modalY = gameArea.y + (gameArea.height - modalH) / 2;

    // 遮罩
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.fillRect(gameArea.x, gameArea.y, gameArea.width, gameArea.height);

    // 卡片
    ctx.beginPath();
    ctx.roundRect(modalX, modalY, modalW, modalH, 16);
    ctx.fillStyle = '#1e1e3a';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // 标题
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 18px sans-serif';
    ctx.fillText(title, modalX + modalW / 2, modalY + modalH * 0.3);

    // 消息
    ctx.fillStyle = '#aaa';
    ctx.font = '14px sans-serif';
    ctx.fillText(message, modalX + modalW / 2, modalY + modalH * 0.5);

    // 取消按钮 + 确认按钮（自适应宽度，确保能放进弹窗）
    const gap = Math.max(10, modalW * 0.04);
    const totalBtnW = modalW - 40 - gap; // 左右各留 20px 边距
    const btnW = Math.max(60, totalBtnW / 2);
    const btnH = Math.max(34, Math.min(44, modalH * 0.22));
    const btnY = modalY + modalH - btnH - 20;

    const cancelX = modalX + 20;
    ctx.beginPath();
    ctx.roundRect(cancelX, btnY, btnW, btnH, 10);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = `bold ${Math.max(12, Math.floor(btnH * 0.35))}px sans-serif`;
    ctx.fillText(cancelText, cancelX + btnW / 2, btnY + btnH / 2);

    // 确认按钮
    const confirmX = cancelX + btnW + gap;
    ctx.beginPath();
    ctx.roundRect(confirmX, btnY, btnW, btnH, 10);
    ctx.fillStyle = '#ff4444';
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.fillText(confirmText, confirmX + btnW / 2, btnY + btnH / 2);
  }

  // 加载失败弹窗
  drawLoadFailedModal(): void {
    this.drawConfirmModal('加载失败', '关卡加载失败，请重试', '重试', '取消');
  }

  drawLevelSelectModal(levelConfigs: { levelNumber: number; thumbnail?: HTMLImageElement }[], completedLevels: number[]): void {
    const ctx = this.ctx;
    const { gameArea } = this.layout;
    // 内缩 32px（手机屏幕圆角 clip 的半径），确保弹窗四角不会被手机圆角裁掉
    const inset = 32;
    const availW = gameArea.width - inset * 2;
    const availH = gameArea.height - inset * 2;

    // 弹窗尽量占满可用区域，避免居中后顶部/底部落到手机圆角内
    const modalW = availW * 0.92;
    const modalH = availH * 0.92;
    const modalX = gameArea.x + (gameArea.width - modalW) / 2;
    const modalY = gameArea.y + (gameArea.height - modalH) / 2;

    // 遮罩
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.fillRect(gameArea.x, gameArea.y, gameArea.width, gameArea.height);

    // 弹窗卡片（深蓝色实底 + 细边框）
    ctx.beginPath();
    ctx.roundRect(modalX, modalY, modalW, modalH, 16);
    ctx.fillStyle = '#2b2d4a';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // 标题
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px "KaiTi", "Microsoft YaHei", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('选择关卡', modalX + modalW / 2, modalY + 18);

    // 关闭按钮（右上角 X）
    const closeSize = 32;
    const closeX = modalX + modalW - closeSize / 2 - 10;
    const closeY = modalY + closeSize / 2 + 10;
    ctx.beginPath();
    ctx.arc(closeX, closeY, closeSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(closeX - 8, closeY - 8);
    ctx.lineTo(closeX + 8, closeY + 8);
    ctx.moveTo(closeX + 8, closeY - 8);
    ctx.lineTo(closeX - 8, closeY + 8);
    ctx.stroke();

    // 5 列网格
    const cols = 5;
    const gap = 10;
    const gridPadX = 20;
    const gridTop = modalY + 54;
    const gridBottom = modalY + modalH - 16;
    const gridAvailH = gridBottom - gridTop;
    const cellW = (modalW - gridPadX * 2 - gap * (cols - 1)) / cols;
    const cellH = Math.min(cellW, 56);
    const startX = modalX + gridPadX;
    const gridRight = startX + cols * cellW + (cols - 1) * gap; // 网格精确右边界

    const totalRows = Math.ceil(levelConfigs.length / cols);
    const totalContentH = totalRows * (cellH + gap) - gap;
    const maxScroll = Math.max(0, totalContentH - gridAvailH);
    this.levelSelectScrollOffset = Math.max(0, Math.min(this.levelSelectScrollOffset, maxScroll));
    const scrollOffset = this.levelSelectScrollOffset;

    // clip 到网格可视区域
    ctx.save();
    ctx.beginPath();
    ctx.rect(startX, gridTop, gridRight - startX, gridAvailH);
    ctx.clip();

    const unlocked = (n: number) => n === 1 || completedLevels.includes(n - 1);
    const completed = (n: number) => completedLevels.includes(n);

    for (let idx = 0; idx < levelConfigs.length; idx++) {
      const lc = levelConfigs[idx];
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      const x = startX + col * (cellW + gap);
      const y = gridTop + row * (cellH + gap) - scrollOffset;

      if (y + cellH < gridTop - 50 || y > gridBottom + 50) continue;

      const isUnlocked = unlocked(lc.levelNumber);

      ctx.beginPath();
      ctx.roundRect(x, y, cellW, cellH, 8);
      if (isUnlocked) {
        ctx.fillStyle = '#e8e8ec';
      } else {
        ctx.fillStyle = '#9a9aa5';
      }
      ctx.fill();
      ctx.strokeStyle = isUnlocked ? '#c0c0c8' : '#7a7a85';
      ctx.lineWidth = 1;
      ctx.stroke();

      if (isUnlocked) {
        ctx.fillStyle = completed(lc.levelNumber) ? '#d4a017' : '#2a2a3a';
        ctx.font = `bold ${Math.floor(cellH * 0.55)}px "KaiTi", "Microsoft YaHei", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${lc.levelNumber}`, x + cellW / 2, y + cellH / 2);

        if (completed(lc.levelNumber)) {
          ctx.fillStyle = '#4CAF50';
          ctx.font = `bold ${Math.floor(cellH * 0.28)}px sans-serif`;
          ctx.fillText('✓', x + cellW / 2, y + cellH - cellH * 0.22);
        }
      } else {
        ctx.fillStyle = 'rgba(60,60,70,0.5)';
        ctx.font = `bold ${Math.floor(cellH * 0.5)}px "KaiTi", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${lc.levelNumber}`, x + cellW / 2, y + cellH / 2 + cellH * 0.08);
      }
    }
    ctx.restore();
  }

  // 按钮点击检测 - 返回点击的UI元素类型
  hitTestUI(sx: number, sy: number): string | null {
    const { gameArea, topBarHeight } = this.layout;
    const mainBarH = Math.floor(topBarHeight * 0.6);
    const iconSize = Math.floor(mainBarH * 0.64); // 和 drawTopBar 里的 size 对应
    const padding = Math.max(10, iconSize * 0.36);
    const buttonGap = Math.max(6, iconSize * 0.2);

    // 第一层主栏按钮检测
    if (sy >= gameArea.y && sy <= gameArea.y + mainBarH) {
      const cx = gameArea.x;
      const musicX = cx + padding + iconSize / 2;
      const restartX = musicX + iconSize / 2 + buttonGap + iconSize / 2;
      const selectX = cx + gameArea.width - padding - iconSize / 2;

      const hitCircleBtn = (x: number) => {
        const dx = sx - x;
        const dy = sy - (gameArea.y + mainBarH / 2);
        const r = iconSize / 2 + 4;
        return dx * dx + dy * dy <= r * r;
      };

      if (hitCircleBtn(musicX)) return 'music';
      if (hitCircleBtn(restartX)) return 'restart';
      if (hitCircleBtn(selectX)) return 'levelSelect';
    }

    // 道具区（最底部）
    const { itemAreaHeight } = this.layout;
    const itemY = gameArea.y + gameArea.height - itemAreaHeight;
    const buttonSize = Math.floor(itemAreaHeight * 0.8);
    const gap = Math.floor(buttonSize * 0.6);
    const totalWidth = buttonSize * 2 + gap;
    const startX = gameArea.x + (gameArea.width - totalWidth) / 2;
    const cy = itemY + itemAreaHeight / 2;

    if (this.hitRect(sx, sy, startX, cy - buttonSize / 2 - 4, buttonSize + 8, buttonSize + 8)) return 'magnet';
    if (this.hitRect(sx, sy, startX + buttonSize + gap, cy - buttonSize / 2 - 4, buttonSize + 8, buttonSize + 8)) return 'hint';

    return null;
  }

  private hitCircle(sx: number, sy: number, cx: number, cy: number, r: number): boolean {
    const dx = sx - cx;
    const dy = sy - cy;
    return dx * dx + dy * dy <= r * r;
  }

  private hitRect(sx: number, sy: number, rx: number, ry: number, rw: number, rh: number): boolean {
    return sx >= rx && sx <= rx + rw && sy >= ry && sy <= ry + rh;
  }

  // 检查弹窗按钮点击
  hitTestModal(sx: number, sy: number, modalType: 'won' | 'lost' | 'levelSelect' | 'confirm' | 'loadFailed', totalLevels?: number, configs?: { levelNumber: number }[]): string | null {
    const { gameArea } = this.layout;
    if (modalType === 'won') {
      const modalW = gameArea.width * 0.8;
      const modalH = gameArea.height * 0.7;
      const modalY = gameArea.y + (gameArea.height - modalH) / 2;
      const btnW = 140;
      const btnH = 48;
      const btnX = gameArea.x + (gameArea.width - btnW) / 2;
      const btnY = modalY + modalH - btnH - 25;
      if (this.hitRect(sx, sy, btnX, btnY, btnW, btnH)) return 'nextLevel';
    } else if (modalType === 'lost') {
      const modalW = gameArea.width * 0.7;
      const modalH = gameArea.height * 0.5;
      const modalY = gameArea.y + (gameArea.height - modalH) / 2;
      const btnW = 140;
      const btnH = 48;
      const btnX = gameArea.x + (gameArea.width - btnW) / 2;
      const btnY = modalY + modalH - btnH - 25;
      if (this.hitRect(sx, sy, btnX, btnY, btnW, btnH)) return 'restart';
    } else if (modalType === 'confirm' || modalType === 'loadFailed') {
      const modalW = gameArea.width * 0.65;
      const modalH = gameArea.height * 0.35;
      const modalX = gameArea.x + (gameArea.width - modalW) / 2;
      const modalY = gameArea.y + (gameArea.height - modalH) / 2;
      const gap = Math.max(10, modalW * 0.04);
      const totalBtnW = modalW - 40 - gap;
      const btnW = Math.max(60, totalBtnW / 2);
      const btnH = Math.max(34, Math.min(44, modalH * 0.22));
      const btnY = modalY + modalH - btnH - 20;

      const cancelX = modalX + 20;
      const confirmX = cancelX + btnW + gap;

      if (this.hitRect(sx, sy, cancelX, btnY, btnW, btnH)) return 'cancel';
      if (this.hitRect(sx, sy, confirmX, btnY, btnW, btnH)) return 'confirm';
    } else if (modalType === 'levelSelect' && configs) {
      const { gameArea } = this.layout;
      const inset = 32;
      const availW = gameArea.width - inset * 2;
      const availH = gameArea.height - inset * 2;
      const modalW = availW * 0.92;
      const modalH = availH * 0.92;
      const modalX = gameArea.x + (gameArea.width - modalW) / 2;
      const modalY = gameArea.y + (gameArea.height - modalH) / 2;

      // 关闭按钮检测
      const closeSize = 32;
      const closeX = modalX + modalW - closeSize / 2 - 10;
      const closeY = modalY + closeSize / 2 + 10;
      const dx = sx - closeX;
      const dy = sy - closeY;
      if (dx * dx + dy * dy <= (closeSize / 2 + 4) ** 2) return 'close';

      // 网格计算（与 drawLevelSelectModal 完全一致）
      const cols = 5;
      const gap = 10;
      const gridPadX = 20;
      const gridTop = modalY + 54;
      const gridBottom = modalY + modalH - 16;
      const gridAvailH = gridBottom - gridTop;
      const cellW = (modalW - gridPadX * 2 - gap * (cols - 1)) / cols;
      const cellH = Math.min(cellW, 56);
      const startX = modalX + gridPadX;
      const scrollOffset = this.levelSelectScrollOffset;

      for (let idx = 0; idx < configs.length; idx++) {
        const col = idx % cols;
        const row = Math.floor(idx / cols);
        const x = startX + col * (cellW + gap);
        const y = gridTop + row * (cellH + gap) - scrollOffset;
        if (y + cellH < gridTop || y > gridBottom) continue;
        if (this.hitRect(sx, sy, x, y, cellW, cellH)) {
          return `level_${configs[idx].levelNumber}`;
        }
      }
    }
    return null;
  }

  // 设置查看道具数据和过期时间
  setHintData(data: typeof this.hintData, durationMs: number = 5000): void {
    this.hintData = data;
    this.hintExpireTime = Date.now() + durationMs;
  }

  getHintData(): typeof this.hintData {
    if (this.hintData && Date.now() < this.hintExpireTime) {
      return this.hintData;
    }
    this.hintData = null;
    return null;
  }
}
